import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { CheckIcon, Flower2Icon, LeafIcon, BrainIcon, HeartIcon, SwordIcon, BookOpenIcon, MusicIcon, UsersIcon, FlameIcon } from 'lucide-react';
const FeaturePreferences: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(assessmentData.appFeatures || []);
  const featureOptions = [{
    id: 'daily-reflections',
    title: 'Daily Reflections',
    description: 'Personalized prompts for introspection and growth',
    icon: BrainIcon,
    color: 'bg-blue-500'
  }, {
    id: 'guided-meditations',
    title: 'Guided Meditations',
    description: 'Audio journeys for healing and spiritual connection',
    icon: HeartIcon,
    color: 'bg-pink-500'
  }, {
    id: 'garden-quests',
    title: 'Garden Quests',
    description: 'Interactive challenges to restore your sacred gardens',
    icon: SwordIcon,
    color: 'bg-green-500'
  }, {
    id: 'wisdom-library',
    title: 'Wisdom Library',
    description: 'Curated teachings on healing and personal growth',
    icon: BookOpenIcon,
    color: 'bg-purple-500'
  }, {
    id: 'healing-soundscapes',
    title: 'Healing Soundscapes',
    description: 'Ambient audio designed for specific healing frequencies',
    icon: MusicIcon,
    color: 'bg-cyan-500'
  }, {
    id: 'community-circles',
    title: 'Community Circles',
    description: 'Connect with others on similar healing journeys',
    icon: UsersIcon,
    color: 'bg-orange-500'
  }, {
    id: 'progress-tracking',
    title: 'Progress Tracking',
    description: 'Visualize your growth and healing journey',
    icon: FlameIcon,
    color: 'bg-red-500'
  }];
  const toggleFeature = (featureId: string) => {
    if (selectedFeatures.includes(featureId)) {
      setSelectedFeatures(selectedFeatures.filter(id => id !== featureId));
    } else {
      setSelectedFeatures([...selectedFeatures, featureId]);
    }
  };
  const handleContinue = () => {
    updateAssessmentData({
      appFeatures: selectedFeatures
    });
    setCurrentStep(currentStep + 1);
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-572">
      <AnimatedBackground type="cosmic" data-id="element-573" />
      <ParticleEffect count={50} data-id="element-574" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-575">
        <LeafIcon size={80} className="text-green-200" data-id="element-576" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-577">
        <LeafIcon size={100} className="text-green-200" data-id="element-578" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-579">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-580" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-581">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-582">
          <span className="text-pink-300" data-id="element-583">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={3} data-id="element-584" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-585">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-586"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-587"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-588">
            Your Sacred Garden Features
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-589">
            Select the features that would most support your healing journey.
            You can access all features later, but we'll prioritize your
            selections.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 relative z-10" data-id="element-590">
            {featureOptions.map(feature => {
            const Icon = feature.icon;
            return <button key={feature.id} onClick={() => toggleFeature(feature.id)} className={`
                    flex items-start p-4 rounded-xl text-left transition-all
                    ${selectedFeatures.includes(feature.id) ? 'bg-white bg-opacity-20 border border-white border-opacity-60' : 'bg-white bg-opacity-5 border border-white border-opacity-20 hover:bg-opacity-10'}
                  `} data-id="element-591">
                  <div className={`
                    w-10 h-10 rounded-full mr-3 flex-shrink-0 flex items-center justify-center
                    ${feature.color} ${selectedFeatures.includes(feature.id) ? 'ring-2 ring-white' : ''}
                  `} data-id="element-592">
                    <Icon size={20} className="text-white" data-id="element-593" />
                  </div>
                  <div data-id="element-594">
                    <div className="flex items-center" data-id="element-595">
                      <h3 className="font-medium text-white" data-id="element-596">
                        {feature.title}
                      </h3>
                      {selectedFeatures.includes(feature.id) && <CheckIcon size={16} className="text-green-400 ml-2" data-id="element-597" />}
                    </div>
                    <p className="text-white text-opacity-80 text-sm mt-1" data-id="element-598">
                      {feature.description}
                    </p>
                  </div>
                </button>;
          })}
          </div>
          <button onClick={handleContinue} className="w-full py-3 rounded-xl transition-all duration-300 text-white font-semibold bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer relative z-10" data-id="element-599">
            Continue Your Journey
          </button>
        </div>
      </div>
    </div>;
};
export default FeaturePreferences;